import React from "react";
import { Image, View, StyleSheet } from "react-native";
import { useFieldArray, useForm, Controller, useWatch } from "react-hook-form";
import { IconInput } from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useBasicInfo } from "../BasicInfoContext";
import { useNavigation } from "@react-navigation/native";
import { FileText, Pill, Plus, X } from "lucide-react-native";
import { BasicInfoQuestionLayout } from "../components";
import { CardSurface, DocumentRow, IconButton } from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";

const medicationPillIllustration =
  require("@src/apps/moon/assets/images/pill-capsule.png") as number;

type MedicationAllergy = {
  id?: string;
  name: string;
  comments: string;
};

type AllergiesForm = {
  allergies: MedicationAllergy[];
  toDelete: { id: string }[];
};

export function AllergiesDetailsScreen() {
  const t = useTranslations();
  const { state, updateState, next } = useBasicInfo("AllergiesDetails");
  const navigation = useNavigation();
  const { control, handleSubmit, getValues, setValue } = useForm<AllergiesForm>(
    {
      defaultValues: {
        allergies:
          state.allergies && state.allergies.length > 0
            ? state.allergies.map((a) => ({
                id: a.id ?? "",
                name: a.name ?? "",
                comments: a.comments ?? "",
              }))
            : [{ name: "", comments: "" }],
        toDelete: [],
      },
      mode: "all",
    },
  );

  const { fields, append, remove } = useFieldArray({
    control,
    keyName: "_id",
    name: "allergies",
  });
  const allergyValues = useWatch({ control, name: "allergies" });
  const hasNamedAllergy = allergyValues?.some(
    (allergy) => allergy.name.trim().length > 0,
  );

  const onSubmit = (data: AllergiesForm) => {
    updateState({
      allergies: [
        ...data.allergies
          .filter((a) => a.name.trim() !== "")
          .map((a) => ({
            ...a,
            allergy: "medications" as const,
          })),
        ...getValues("toDelete").map((d) => ({
          id: d.id,
          op: "delete" as const,
        })),
      ],
    });
    next();
  };

  return (
    <BasicInfoQuestionLayout
      route="AllergiesDetails"
      title={t("med_history.basic_info.allergies_details.title")}
      subtitle={t("med_history.basic_info.allergies_details.subtitle")}
      helpText={t("med_history.basic_info.help.allergy_details")}
      onBack={() => navigation.goBack()}
      onSave={handleSubmit(onSubmit)}
      saveDisabled={!hasNamedAllergy}
      saveLabel={t("med_history.shared.next")}
    >
      <View style={styles.formContainer}>
        {fields.map((field, index) => (
          <CardSurface key={field._id} style={styles.fieldContainer}>
            {fields.length > 1 && (
              <View style={styles.fieldActions}>
                <IconButton
                  accessibilityLabel={t(
                    "med_history.basic_info.allergies_details.remove",
                    { params: { n: String(index + 1) } },
                  )}
                  action="med_history.basic_info.allergies_details.remove"
                  onPress={() => {
                    if (field.id) {
                      setValue("toDelete", [
                        ...getValues("toDelete"),
                        { id: field.id },
                      ]);
                    }
                    remove(index);
                  }}
                  size={24}
                  surface="bare"
                  pressedOpacity={0.2}
                >
                  <X size={16} color="#ef4444" />
                </IconButton>
              </View>
            )}

            <View style={styles.inputGroup}>
              <Controller
                control={control}
                name={`allergies.${index}.name`}
                render={({ field: { onChange, value } }) => (
                  <IconInput
                    icon={Pill}
                    label={t(
                      "med_history.medical.medications_vaccines.medication_name_label",
                    )}
                    placeholder={t(
                      "med_history.basic_info.allergies_details.name_placeholder",
                    )}
                    value={value || ""}
                    onChangeText={onChange}
                  />
                )}
              />
            </View>

            <View style={styles.inputGroup}>
              <Controller
                control={control}
                name={`allergies.${index}.comments`}
                render={({ field: { onChange, value } }) => (
                  <IconInput
                    icon={FileText}
                    label={t(
                      "med_history.medical.medications_vaccines.comments_label",
                    )}
                    placeholder={t(
                      "med_history.basic_info.allergies_details.comments_placeholder",
                    )}
                    value={value || ""}
                    onChangeText={onChange}
                    multiline
                    numberOfLines={3}
                  />
                )}
              />
            </View>
          </CardSurface>
        ))}

        <DocumentRow
          onPress={() => append({ name: "", comments: "" })}
          accessibilityLabel={t(
            "med_history.basic_info.allergies_details.add_another",
          )}
          leading={
            <Image
              source={medicationPillIllustration}
              resizeMode="contain"
              style={styles.addIllustration}
            />
          }
          title={t("med_history.basic_info.allergies_details.add_another")}
          trailing={
            <Plus size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
          }
          showChevron={false}
          style={styles.addRow}
        />
      </View>
    </BasicInfoQuestionLayout>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    width: "100%",
    marginBottom: 40,
  },
  fieldContainer: {
    marginBottom: 24,
    padding: 16,
  },
  fieldActions: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    minHeight: 24,
  },
  inputGroup: {
    marginBottom: 12,
  },
  addRow: {
    width: "100%",
  },
  addIllustration: {
    width: 34,
    height: 34,
  },
});

export default AllergiesDetailsScreen;
